import { useEffect, useRef, useState } from "react";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useHistoryBackedOverlay } from "@/hooks/useHistoryBackedOverlay";
import { track, type EventSurface } from "@/lib/analytics";
import { recordTap } from "@/lib/tapHistory";
import { venuePath, venueSlugOf } from "@/lib/venueUrl";
import { useShowcase } from "@/services/showcase";
import type { Restaurant, RestaurantId } from "@/types/restaurant";

/**
 * The venue detail modal: which venue is open, its keyboard/Back/scroll
 * handling, and the one funnel every surface routes venue-opens through.
 *
 * Venue pages (venue-detail-offers.md): a tap puts the venue's own URL
 * (`/cluj-pomet`) in the address bar over the feed, without routing. A visit
 * that STARTS on that URL ("deep", from Google or a shared link) opens the
 * same modal through `openFromUrl`; closing it calls `onDeepClose`, which
 * replaces the URL with the city feed's.
 */
export function useVenueDetail({ onDeepClose }: { onDeepClose?: () => void } = {}) {
  const showcase = useShowcase();
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [deep, setDeep] = useState(false);
  // Keyboard containment for the detail modal (APG dialog pattern).
  const detailRef = useRef<HTMLDivElement>(null);
  useFocusTrap(detailRef, selectedRestaurant !== null);
  // Hardware/browser Back closes the detail and keeps the visitor in their city
  // feed, instead of popping /app?city=X and bouncing them to the city gate.
  // The demo keeps its own URLs (its venues have no slugs).
  useHistoryBackedOverlay(
    selectedRestaurant !== null && !deep,
    () => setSelectedRestaurant(null),
    showcase || !selectedRestaurant ? null : venuePath(selectedRestaurant.city, venueSlugOf(selectedRestaurant)),
  );

  // Venues the visitor opened this session — the engagement gate for the passive
  // dwell detector (a passer-by who never opened a venue can't be converted).
  const openedVenues = useRef<Set<RestaurantId>>(new Set());
  const open = (r: Restaurant, surface?: EventSurface) => {
    // The demo's taps must not bias this device's real taste history.
    if (!showcase) recordTap(r);
    openedVenues.current.add(r.id);
    // Funnel event — every surface routes venue-opens through here.
    track("venue_view", { venue_id: r.id, city: r.city ?? undefined, surface });
    setSelectedRestaurant(r);
  };
  const selectRestaurant = (r: Restaurant, surface?: EventSurface) => {
    setDeep(false);
    open(r, surface);
  };
  const openFromUrl = (r: Restaurant) => {
    setDeep(true);
    open(r);
  };

  const deepCloseRef = useRef(onDeepClose);
  deepCloseRef.current = onDeepClose;
  const closeRef = useRef(() => {});
  closeRef.current = () => {
    setSelectedRestaurant(null);
    if (deep) {
      setDeep(false);
      deepCloseRef.current?.();
    }
  };
  const closeDetail = () => closeRef.current();

  // Lock background scroll + close on Escape while the floor-plan modal is open.
  useEffect(() => {
    if (!selectedRestaurant) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedRestaurant]);

  return { selectedRestaurant, detailRef, openedVenues, selectRestaurant, openFromUrl, closeDetail };
}
